import React, { useCallback, useEffect, useState } from "react";
import { NewModal, NewCreatableSelectList, MainContentPaddingTypes } from "../..";
import { ListStyle_Types, SelectFieldOption_Values } from "../../../Definitions";
import { useApolloClient, useQuery } from "@apollo/client";
import { ADD_DOMAIN_USER, ADD_GROUP_TO_USER, AddDomainUserResponse, AddGroupUserResponse, ALL_DOMAINS, AllUser_Data, Company_Data, USER_GROUPS, UserGroups_Data, UserGroupsResponse } from "../../../Services/Agrisys_GqlDataCaller/UserCalls/GQLCalls/userCalls";
import NewButton from "../../BaseComponents/newButton";

interface CompanyGroupAddModal_Props {
  isModalOpen: boolean;
  closeModal: () => void;
  userlist: AllUser_Data[];
  onUserAdded?: () => void;
}

export default function AssignUserModal({ isModalOpen, closeModal, userlist, onUserAdded } : CompanyGroupAddModal_Props) {
  const client = useApolloClient();
  const { data: allDomainsData } = useQuery(ALL_DOMAINS);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string>("");
  const [selectedUserGroupId, setSelectedUserGroupId] = useState<number | null>(null);
  const [allGroupsData, setAllGroupsData] = useState<UserGroups_Data[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 2000);
      return () => clearTimeout(timer);
    }

    if (isModalOpen) {
      setSelectedUserId(null);
      setSelectedDomain("");
      setSelectedUserGroupId(null);
    }
  }, [error, isModalOpen]);

  useEffect(() => {
    if (!selectedDomain) return;
    const fetchGroupsForDomain = async () => {
      try {
        const res = await client.query<UserGroupsResponse>({
          query: USER_GROUPS,
          variables: { domain: selectedDomain },
          fetchPolicy: "network-only",
          context:{ headers: { tocloud:true }}
        });
        setAllGroupsData(res.data.company.groups);
      } catch (err: any) {
        setAllGroupsData([]);
        setError(err.message || "Failed to fetch groups for domain");
      }
    };
    fetchGroupsForDomain();
  }, [selectedDomain, client]);

  // #region | Select Options
  // User Options
  const userOptions: SelectFieldOption_Values[] = userlist.slice().sort((a, b) => a.username.localeCompare(b.username)).map(user => ({
    label: user.username,
    value: String(user.user_id),
  }));

  // Domain Options
  const domainOptions: SelectFieldOption_Values[] = (
    allDomainsData?.user?.companies?.slice().sort((a: Company_Data, b: Company_Data) => a.name.localeCompare(b.name)).map((d: Company_Data) => ({
      label: d.name + " | " + d.domain,
      value: d.domain,
    })) ?? []
  );

  // Group Options
  const userGroupOptions: SelectFieldOption_Values[] = allGroupsData.map(group => ({
    label: group.name,
    value: String(group.user_group_id),
  }));
  // #endregion

  // #region | Handle assign button
  const assignUser = useCallback(async () => {
    const selectedUser = userlist.find(u => u.user_id === selectedUserId);

    if (!selectedUser?.username || !selectedDomain) {
      setError("Please select a user and domain.");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const userAlreadyHasDomain = selectedUser.user_companies.some(
        (c: Company_Data) => c.domain === selectedDomain
      );
      if (!userAlreadyHasDomain) {
        const domainResp = await client.mutate<AddDomainUserResponse>({
          mutation: ADD_DOMAIN_USER,
          variables: { username: selectedUser.username, domain: selectedDomain },
          context: { clientName: "internal", headers: { tocloud:true } },
          fetchPolicy: "network-only",
        });
        const domainResult = domainResp?.data?.user?.addDomainUser;
        if (!domainResult?.success) {
          setError(domainResult?.message || "Failed to add domain");
          return;
        }
      } else {
        console.info("User already has this domain, skipping domain mutation");
      }

      if (selectedUserGroupId) {
        const groupResp = await client.mutate<AddGroupUserResponse>({
          mutation: ADD_GROUP_TO_USER,
          variables: { 
            username: selectedUser.username, 
            domain: selectedDomain, 
            usergroupid: selectedUserGroupId 
          },
          context: { clientName: "internal", headers: { tocloud:true } },
          fetchPolicy: "network-only",
        });

        const groupResult = groupResp?.data?.user?.addGroupUser;
        
        if (!groupResult?.success) {
          setError(groupResult?.message || "Failed to add user to group");
          return;
        }
      }

      if (onUserAdded) onUserAdded();
      closeModal();
    } catch (err: any) {
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [client, selectedDomain, selectedUserGroupId, closeModal, onUserAdded]);
  // #endregion

  // #region | Select handles
  const handleSelect = (e: SelectFieldOption_Values | undefined, selectListIndex: number) => {
    if (selectListIndex === 0) {
      setSelectedUserId(Number(e?.value));
      setSelectedDomain("");
      setSelectedUserGroupId(null);
      return;
    }
    if (selectListIndex === 1) {
      setSelectedDomain(e?.value ?? "");
      setSelectedUserGroupId(null);
      return;
    }
    if (selectListIndex === 2) {
      setSelectedUserGroupId(Number(e?.value))
    }
  }
  // #endregion

  if (!isModalOpen) return null;

  return (
    <NewModal
      modalState={isModalOpen}
      header={("Assign User")}
      predefinedButtons={{
        onClose: closeModal
      }}
      overrides={{
        mainContentPadding: MainContentPaddingTypes.secondary
      }}
      mainContent={
        <NewCreatableSelectList
          placeholders={["", "", ""]}
          headers={["user", "domain", "group"]}
          options={[userOptions, domainOptions, userGroupOptions]}
          isClearable
          isSearchable
          onChanges={[userOptions, domainOptions, userGroupOptions].map((_, i) => (e) => handleSelect(e, i) )}
          isDisabled={[false, !selectedUserId, (!selectedDomain || allGroupsData.length === 0)]}
          listOverrides={{
            listStyle: ListStyle_Types.vertical,
            listGap: "10px"
          }}
        />
      }
      footerContent={
        <NewButton
          onClick={assignUser}
          content={"Assign"}
        />
      }
      footerMessage={error ?? ""}
    />
  );
}
